'use client';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { RingProSelectionContext } from './RingProSelectionContext';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { getDiscountForStore } from 'content/products/ring-pro/tierPricing';
import type { TierInfo } from './utils/types';
import styles from './TierProgressBar.module.css';

// Module-level flag so the animation only plays once per page session
let hasAnimated = false;

type TierBarData = {
  key: string;
  fill: number;
  state: 'completed' | 'active' | 'future';
} & TierInfo;

export function TierProgressBar() {
  const { tierCampaign, basePrice, currencySymbol, store } = useContext(RingProSelectionContext);

  if (!tierCampaign || !tierCampaign.campaign_active) return null;

  const activeTier = tierCampaign.tier_info[tierCampaign.active_tier];
  if (!activeTier) return null;

  const hasSpots = activeTier.max_order_count !== null;
  const spotsLeft = hasSpots
    ? (activeTier.max_order_count ?? 0) - tierCampaign.total_orders
    : null;

  const tierKeys = Object.keys(tierCampaign.tier_info);
  const activeTierIndex = tierKeys.indexOf(tierCampaign.active_tier);

  const tiers: TierBarData[] = tierKeys.map((key, index) => {
    const tier = tierCampaign.tier_info[key];
    let fill: number;
    let state: 'completed' | 'active' | 'future';

    if (index < activeTierIndex) {
      fill = 100;
      state = 'completed';
    } else if (index === activeTierIndex) {
      const prevMax = index > 0
        ? tierCampaign.tier_info[tierKeys[index - 1]].max_order_count ?? 0
        : 0;
      const capacity = (tier.max_order_count ?? 0) - prevMax;
      fill = capacity > 0
        ? (tierCampaign.orders_in_current_tier / capacity) * 100
        : 100;
      state = 'active';
    } else {
      fill = 0;
      state = 'future';
    }

    return { key, fill, state, ...tier };
  });

  return (
    <TierProgressBarInner
      title={activeTier.title}
      spotsLeft={spotsLeft}
      tiers={tiers}
      basePrice={basePrice}
      currencySymbol={currencySymbol}
      store={store ? String(store) : 'ROW'}
    />
  );
}

function TierProgressBarInner({
  title,
  spotsLeft,
  tiers,
  basePrice,
  currencySymbol,
  store,
}: {
  title: string;
  spotsLeft: number | null;
  tiers: TierBarData[];
  basePrice: number;
  currencySymbol?: React.ReactNode;
  store: string;
}) {
  const activeTier = tiers.find((t) => t.state === 'active');
  const activeFill = activeTier?.fill ?? 0;

  const [displayCount, setDisplayCount] = useState(
    spotsLeft !== null ? (hasAnimated ? spotsLeft : spotsLeft + 8) : 0,
  );
  const [activeBarWidth, setActiveBarWidth] = useState(hasAnimated ? activeFill : 0);
  const formatPriceWithComma = useFormatPrice();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (hasAnimated) return;
    requestAnimationFrame(() => setActiveBarWidth(activeFill));
  }, [activeFill]);

  useEffect(() => {
    if (hasAnimated || spotsLeft === null) return;
    intervalRef.current = setInterval(() => {
      setDisplayCount((prev) => {
        if (prev <= spotsLeft) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          hasAnimated = true;
          return spotsLeft;
        }
        return prev - 1;
      });
    }, 180);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [spotsLeft]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.tierTitle}>{title}</span>
        {spotsLeft !== null && (
          <span className={styles.spotsLeft}>
            <strong>{displayCount}</strong> LEFT
          </span>
        )}
      </div>
      <div className={styles.barsRow}>
        {tiers.map((tier) => {
          const isFuture = tier.state === 'future';
          const isActive = tier.state === 'active';
          const barWidth = isActive ? activeBarWidth : tier.fill;
          const tierDiscount = getDiscountForStore(tier.discount_value, store);

          return (
            <div className={styles.tierColumn} key={tier.key}>
              <div
                className={styles.barTrack}
                data-future={isFuture ? 'true' : undefined}
              >
                <div
                  className={styles.barFill}
                  style={{ '--bar-width': `${barWidth}%` } as React.CSSProperties}
                  data-animate={isActive ? 'true' : undefined}
                />
              </div>
              <div className={styles.tierMarker}>
                <span
                  className={styles.dot}
                  data-active={!isFuture ? 'true' : undefined}
                />
                <span
                  className={styles.price}
                  data-active={!isFuture ? 'true' : undefined}
                >
                  {currencySymbol}
                  {formatPriceWithComma(basePrice - tierDiscount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
